import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';

import { Card, CardHeader } from '@mui/material';
// @mui
import { styled, useTheme } from '@mui/material/styles';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

// components
import { useChart } from '../chart';
// utils
import { fShortenNumber } from '../../utils/formatNumber';

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

PieChart.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  chartColors: PropTypes.arrayOf(PropTypes.string),
  chartData: PropTypes.array,
  chartStyle: PropTypes.object,
  height: PropTypes.number,
  noMarginTop: PropTypes.bool,
};

export default function PieChart({
  title,
  subheader,
  chartColors,
  chartData,
  height = 280,
  noMarginTop,
  ...other
}) {
  const chart_height = height * 1.32;
  const legend_height = height * 0.25;

  const StyledChartWrapper = styled('div')(({ theme }) => ({
    height: chart_height,
    marginTop: !noMarginTop && theme.spacing(5),
    '& .apexcharts-canvas svg': { height: chart_height },
    '& .apexcharts-canvas svg,.apexcharts-canvas foreignObject': {
      overflow: 'visible',
    },
    '& .apexcharts-legend': {
      height: legend_height,
      alignContent: 'center',
      position: 'relative !important',
      borderTop: `solid 1px ${theme.palette.divider}`,
      top: `calc(${chart_height - legend_height}px) !important`,
    },
  }));

  const theme = useTheme();
  const { isSmallScreen } = useResponsiveScreen();

  const chartLabels = chartData?.map((i) => i.label);

  const chartSeries = chartData?.map((i) => i.value);

  const chartOptions = useChart({
    colors: chartColors,
    labels: chartLabels,
    stroke: { colors: [theme.palette.background.paper] },
    legend: { floating: true, horizontalAlign: 'center' },
    dataLabels: {
      enabled: true,
      dropShadow: { enabled: false },
      style: {
        fontSize: isSmallScreen ? '0.675rem' : '0.8rem',
      },
    },
    tooltip: {
      fillSeriesColor: false,
      y: {
        formatter: (seriesName) => fShortenNumber(seriesName),
        title: {
          formatter: (seriesName) => `${seriesName}`,
        },
      },
    },
    plotOptions: {
      pie: { donut: { labels: { show: true } } },
    },
  });

  return (
    <Card {...other}>
      <CardHeader
        title={title}
        subheader={subheader}
        sx={{
          '& .MuiCardHeader-title': { fontSize: isSmallScreen && '0.8rem' },
          '& .MuiCardHeader-subheader': { fontSize: isSmallScreen && '0.7rem' },
        }}
      />

      <StyledChartWrapper dir="ltr">
        <ReactApexChart
          type="pie"
          series={chartSeries}
          options={chartOptions}
          height={height}
        />
      </StyledChartWrapper>
    </Card>
  );
}
